import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  Upload,
  FileCheck,
  ShieldCheck,
  Clock,
  XCircle,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { safeFetch } from "../utils/safeFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DocumentUploadPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isWelcome = params.get("welcome") === "1";
  const { token, isAuthenticated, loading: authLoading, refreshUser } = useAuth();

  const fileInputRef = useRef(null);
  const [docStatus, setDocStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate("/", { replace: true });
      return;
    }
    (async () => {
      try {
        const res = await safeFetch(`${API_URL}/api/auth/document-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setDocStatus(await res.json());
      } catch {
        /* ignore */
      }
    })();
  }, [authLoading, isAuthenticated, token, navigate]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf"];
    if (!allowed.includes(file.type)) {
      setError("JPG, PNG, GIF, WEBP, PDF 형식만 지원합니다.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("파일 크기는 10MB 이하만 가능합니다.");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("doc_type", "marriage_certificate");
      const res = await safeFetch(`${API_URL}/api/auth/upload-document`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "업로드 실패");
      setDocStatus({ status: "pending", doc_type: "marriage_certificate" });
      toast.success("서류가 제출되었습니다. 관리자 승인을 기다려주세요.");
      if (refreshUser) await refreshUser();
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const status = docStatus?.status || "none";

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white py-16" data-testid="document-upload-page">
      <div className="max-w-xl mx-auto px-4 sm:px-6">
        {isWelcome && (
          <div className="mb-6 flex items-center gap-3 px-5 py-4 rounded-2xl bg-burgundy-50 border border-burgundy-100" data-testid="welcome-banner">
            <CheckCircle2 className="w-5 h-5 text-burgundy-500 flex-shrink-0" />
            <p className="text-sm text-burgundy-700">
              자기소개서 작성이 완료되었습니다. 마지막으로 본인 인증 서류를 제출해주세요.
            </p>
          </div>
        )}

        <div className="text-center mb-8">
          <p className="text-[11px] font-bold tracking-[0.3em] text-burgundy-500 uppercase mb-3">Verification</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-3">서류 인증</h1>
          <p className="text-stone-500 text-sm leading-relaxed">
            신뢰할 수 있는 만남을 위해 혼인관계증명서(미혼/이혼/사별 증명)를 제출해주세요.
          </p>
        </div>

        <div className="bg-white rounded-3xl border border-stone-200 shadow-sm p-7">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-full bg-cyan-100 flex items-center justify-center">
              <FileCheck className="w-5 h-5 text-cyan-600" />
            </div>
            <div>
              <p className="font-semibold text-stone-800 text-sm">혼인관계증명서</p>
              <p className="text-xs text-stone-400">제출 시 10,000P가 지급됩니다</p>
              <p className="text-[10px] text-emerald-600 mt-0.5">서류는 암호화 저장되며, 확인 후 즉시 삭제됩니다</p>
            </div>
          </div>

          {error && (
            <p className="mb-4 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-4 py-2.5" data-testid="upload-error">
              {error}
            </p>
          )}

          {(status === "none") && (
            <div>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                onChange={handleUpload}
                className="hidden"
                data-testid="doc-file-input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full flex items-center justify-center gap-2 py-4 border-2 border-dashed border-stone-300 rounded-2xl text-stone-500 hover:border-cyan-400 hover:text-cyan-600 hover:bg-cyan-50 transition-all disabled:opacity-50"
                data-testid="doc-upload-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "업로드 중..." : "혼인관계증명서 업로드"}
              </button>
              <p className="text-xs text-stone-400 mt-2 text-center">JPG, PNG, PDF 지원 (최대 10MB)</p>
            </div>
          )}

          {status === "pending" && (
            <div>
              <div className="flex items-center gap-2 py-4 px-4 bg-amber-50 border border-amber-200 rounded-2xl mb-3" data-testid="doc-status-pending">
                <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
                <span className="text-sm text-amber-700">서류 심사 중입니다. 관리자 승인을 기다려주세요.</span>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                onChange={handleUpload}
                className="hidden"
                data-testid="doc-file-input-repending"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full flex items-center justify-center gap-2 py-3 border border-stone-300 rounded-2xl text-stone-500 hover:border-cyan-400 hover:text-cyan-600 transition-all text-sm disabled:opacity-50"
                data-testid="doc-repending-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                서류 다시 첨부하기
              </button>
              <p className="text-xs text-stone-400 mt-2 text-center">잘못 올렸거나 파일에 문제가 있으면 다시 첨부해 교체할 수 있어요</p>
            </div>
          )}

          {status === "approved" && (
            <div className="flex items-center gap-2 py-4 px-4 bg-green-50 border border-green-200 rounded-2xl" data-testid="doc-status-approved">
              <ShieldCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span className="text-sm text-green-700 font-medium">서류인증 완료 (10,000P 지급 완료)</span>
            </div>
          )}

          {status === "rejected" && (
            <div>
              <div className="flex items-center gap-2 py-3 px-4 bg-red-50 border border-red-200 rounded-2xl mb-3" data-testid="doc-status-rejected">
                <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <div>
                  <span className="text-sm text-red-700">서류가 반려되었습니다.</span>
                  {docStatus?.review_note && (
                    <p className="text-xs text-red-500 mt-0.5">사유: {docStatus.review_note}</p>
                  )}
                </div>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                onChange={handleUpload}
                className="hidden"
                data-testid="doc-file-input-retry"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full flex items-center justify-center gap-2 py-3 border border-stone-300 rounded-2xl text-stone-500 hover:border-cyan-400 hover:text-cyan-600 transition-all text-sm disabled:opacity-50"
                data-testid="doc-retry-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                다시 제출하기
              </button>
            </div>
          )}
        </div>

        <button
          onClick={() => navigate("/community", { replace: true })}
          className="mt-6 w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-burgundy-600 hover:bg-burgundy-700 text-white font-semibold text-sm transition"
          data-testid="go-community-btn"
        >
          {status === "none" ? "나중에 하고 커뮤니티로 이동" : "커뮤니티로 이동"}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
